/**
 * Cloudinary upload helpers for the member profile-photo flow.
 *
 * Mirrors `uploadProfilePhotoOnlyWebFile` and `uploadMemberCardAssetWebFile`
 * from membership-mobile-app/src/utils/uploadImage.ts so the web client
 * writes the same two assets the mobile client does:
 *
 *   1. `profile-photos` preset, auto-generated public_id - drives the
 *      in-app avatar. Uploaded BEFORE AI Vision validation so a rejected
 *      photo never reaches the print pipeline.
 *   2. `member-card` preset, `public_id = pgaId` - print-ready asset
 *      consumed by PBD. Only written after `validateProfilePhoto` approves.
 *
 * See pgahq/membership-mobile-app#1255 for why the two uploads must stay
 * sequenced (not parallel) around the validation gate.
 */

const CLOUDINARY_API_BASE = "https://api.cloudinary.com/v1_1"

const cloudName = () => process.env.REACT_APP_CLOUDINARY_CLOUD_NAME

const apiUrl = () => `${CLOUDINARY_API_BASE}/${cloudName()}/image/upload`

const optimizedDeliveryUrl = publicId =>
  `https://res.cloudinary.com/${cloudName()}/image/upload/f_auto,q_auto/${publicId}`

// Tag an upload failure so the hook can branch on it without string sniffing.
export const CLOUDINARY_RATE_LIMITED = "CLOUDINARY_RATE_LIMITED"

const isRateLimitBody = body =>
  /Rate Limit Exceeded/i.test(body) ||
  /Cloudinary AI Content Analysis/i.test(body) ||
  /monthly billing usage/i.test(body)

const postCloudinaryUpload = async (file, { uploadPreset, folder, publicId }) => {
  const formData = new FormData()
  formData.append("file", file)
  formData.append("upload_preset", uploadPreset)
  if (folder) formData.append("folder", folder)
  if (publicId) formData.append("public_id", publicId)

  const response = await fetch(apiUrl(), { method: "POST", body: formData })
  if (!response.ok) {
    const errorText = await response.text().catch(() => "<unreadable>")
    // Cloudinary returns 420 with a JSON body when incoming AI moderation
    // exhausts the monthly quota. Tag this case so callers can fall back
    // to a non-moderated preset instead of failing the whole flow.
    if (response.status === 420 || isRateLimitBody(errorText)) {
      const err = new Error(
        `Cloudinary upload failed: ${response.status} - ${errorText}`,
      )
      err.code = CLOUDINARY_RATE_LIMITED
      throw err
    }
    throw new Error(`Cloudinary upload failed: ${response.status} - ${errorText}`)
  }
  const result = await response.json()
  if (!result.public_id || !result.secure_url) {
    throw new Error(
      "Cloudinary upload succeeded but response is missing public_id or secure_url",
    )
  }
  return { publicId: result.public_id, secureUrl: result.secure_url }
}

export const uploadProfilePhotoOnly = async file => {
  const result = await postCloudinaryUpload(file, {
    uploadPreset: process.env.REACT_APP_CLOUDINARY_PROFILE_PHOTOS_UPLOAD_PRESET,
    folder: "profile-photos",
  })
  return { profileUrl: optimizedDeliveryUrl(result.publicId) }
}

/**
 * Fallback path for when the m360-profile-photos preset's incoming AI
 * moderation exhausts its monthly quota. Uses the legacy
 * `REACT_APP_CLOUDINARY_UPLOAD_PRESET` (folder `member-images`) which has
 * no moderation gate and has been the production preset for this app
 * pre-mobile-parity. The hook still calls `validateProfilePhoto`
 * afterwards, which fails open in this same scenario.
 */
export const uploadProfilePhotoFallback = async file => {
  // Keep the `profile-photos` folder so the resulting delivery URL is
  // path-shaped the same as the primary preset's output. The backend
  // updatePerson resolver only accepts profilePhotoUrls under that folder.
  const result = await postCloudinaryUpload(file, {
    uploadPreset: process.env.REACT_APP_CLOUDINARY_UPLOAD_PRESET,
    folder: "profile-photos",
  })
  return { profileUrl: optimizedDeliveryUrl(result.publicId) }
}

export const uploadMemberCardAsset = async (file, { pgaId }) => {
  const result = await postCloudinaryUpload(file, {
    uploadPreset: process.env.REACT_APP_CLOUDINARY_MEMBER_CARD_UPLOAD_PRESET,
    publicId: pgaId,
  })
  return { memberCardUrl: result.secureUrl }
}
